/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { startCase } from "lodash";
import { useSelector } from "react-redux";
import * as moment from "moment-timezone";
import { Controller, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup
} from "@mui/material";

import {
  isEmail,
  validate,
  isISODateString,
  isADDate,
  isBSDate
} from "../../../helpers/validators";
import {
  convertADtoBS,
  convertBStoAD
} from "../../../components/Calendar/functions/calendarFunctions";

import Can from "../../Policy/Can";
import useCan from "../../../hooks/useCan";
import { RootState } from "../../../store";
import Panel from "../../../components/Panel";
import { ALL_DEPARTMENT } from "./SPCreateEdit";
import { t, tl } from "../../../components/translate";
import useGetDepartments from "../../../hooks/department";
import ToggleInputDate from "../../../components/ToggleADBS";
import useIsAccountSubscribed from "../../../hooks/accounts";
import { ledgerFindBySubLedgerType } from "../../accounts/hooks";
import MaterialUiPhoneNumber from "../../../components/PhoneNumber";
import { UserGroupTypes, Employee } from "../../../interfaces/User";
import FormTextField from "../../../components/FormTextField/FormTextField";
import { SubLedgerTypes, TransactionType } from "../../../interfaces/Accounts";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import useCurrentResourceCentreShifts from "../../../hooks/useCurrentResourceCentreShifts";
import { Gender } from "../../../interfaces/ClientInterface";

interface PropType {
  onClose?: () => void;
  createMode?: boolean;
  employee?: Partial<Employee>;
  isEmployeeOnlyTab?: boolean;
  onSave?: (data: Partial<Employee>) => void;
  departmentSubscription?: boolean;
}

export default function EmployeeCreateEdit({
  onClose,
  createMode,
  employee,
  isEmployeeOnlyTab,
  onSave,
  departmentSubscription
}: PropType): React.ReactNode {
  const shifts = useCurrentResourceCentreShifts();
  const isAccountSubscribed = useIsAccountSubscribed();
  const employeeLedger = ledgerFindBySubLedgerType(SubLedgerTypes.EMPLOYEE);

  const departments = useGetDepartments({
    showDeletedDepartments: false,
    resourceCentreId: employee?.resourceCentreId
  });

  const resourceCentrePermissionGroups =
    useSelector((s: RootState) => s.resourceCentreUserGroups.collection) || [];

  const canChangePermission = useCan("clinic:changePermissionGroups", { superAdminPass: true });

  const defaultValues: Partial<Employee> = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    departmentId: ALL_DEPARTMENT,
    userGroup: "",
    panNumber: "",
    dateOfJoining: new Date().toISOString(),
    dateOfBirth: undefined,
    address: "",
    gender: "1",
    maritalStatus: undefined,
    taxOptedStatus: undefined,
    emergencyContact: { contactName: "", contactNumber: "" },
    bankDetails: {
      name: "",
      address: "",
      accountNumber: "",
      beneficiaryName: ""
    },
    defaultShiftId: null,
    defaultWeeklyOffs: [],
    biometricDeviceCode: "",
    ...(isAccountSubscribed && {
      openingBalance: undefined,
      openingBalanceDate: undefined,
      transactionType: undefined,
      ledgerId: employeeLedger ? employeeLedger.id : undefined
    })
  } as Partial<Employee>;

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors }
  } = useForm<FieldValues>({
    defaultValues: employee || { ...defaultValues },
    mode: "onChange"
  });

  const { openingBalanceDate, departmentId } = watch();

  const validateEmail = (value: string) => {
    const { isValid, messages } = validate(value, [isEmail({ msg: "Email address invalid" })]);
    return isValid || messages[0];
  };

  const userGroupOptions = resourceCentrePermissionGroups
    .filter((group: any) => group.userGroupType === UserGroupTypes.resourceCentreEmployee)
    .sort((a: { id: number }, b: { id: number }) => a.id - b.id)
    .map((group: any) => ({
      value: group.name,
      label: startCase(group.name),
      description: group.description || "",
      isDepartmentGroup: group.isDepartment || false
    }));

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    const cleaned: Partial<Employee> = {
      ...data,
      departmentId: data.departmentId === ALL_DEPARTMENT ? null : data.departmentId
    } as Partial<Employee>;
    onSave?.(cleaned);
  };

  return (
    <Can policyAccessKey="clinic:createAdmin" superAdminPass>
      <Panel
        onClose={() => {
          onClose?.();
        }}
        title={createMode ? t("employee.createEmployee") : tl("employee.updateEmployee")}
        footer={
          <Box display="flex" width="100%" justifyContent="space-between">
            <Box />
            <Box display="flex" justifyContent="space-between" alignItems="center" mr="32px">
              <Button
                onClick={() => {
                  onClose?.();
                }}
                style={{ marginRight: "32px" }}
                data-testmation="employeeCancel"
              >
                <Typography>{tl("cancel")}</Typography>
              </Button>
              <Button
                form="adminForm"
                type="submit"
                color="primary"
                variant="contained"
                onClick={handleSubmit(onSubmit)}
                data-testmation="employeeCreateSave"
              >
                {createMode ? `${t("Create")}` : "Save"}
              </Button>
            </Box>
          </Box>
        }
      >
        <Box width="100%">
          <form id="adminForm">
            <Grid container columnSpacing={2} rowSpacing={2}>
              {departmentSubscription && (
                <Grid size={12}>
                  <FormTextField
                    dataTestmation="departmentId"
                    name="departmentId"
                    control={control}
                    label={t("department")}
                    errors={errors}
                    fullWidth
                    select
                    options={[
                      { value: ALL_DEPARTMENT, label: startCase(ALL_DEPARTMENT) },
                      ...departments.map((d) => ({ value: d.id, label: d.name }))
                    ]}
                    rules={{ required: { value: true, message: "Department is required" } }}
                  />
                </Grid>
              )}

              {canChangePermission && !isEmployeeOnlyTab && (
                <Grid size={12}>
                  <FormTextField
                    dataTestmation="userGroup"
                    name="userGroup"
                    control={control}
                    label={t("userGroup")}
                    errors={errors}
                    fullWidth
                    select
                    options={userGroupOptions.filter((group: any) =>
                      departmentId === ALL_DEPARTMENT
                        ? !group.isDepartmentGroup
                        : group.isDepartmentGroup
                    )}
                    rules={{
                      required: { value: !isEmployeeOnlyTab, message: "User group is required" }
                    }}
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }}>
              <Chip label="Personal Details" size="small" />
            </Divider>
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  dataTestmation="firstName"
                  placeholder={t("EnterFName")}
                  name="firstName"
                  control={control}
                  label={t("FName")}
                  errors={errors}
                  fullWidth
                  rules={{ required: { value: true, message: "First name is required" } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  dataTestmation="lastName"
                  placeholder={t("EnterLName")}
                  name="lastName"
                  control={control}
                  label={t("LName")}
                  errors={errors}
                  fullWidth
                  rules={{ required: { value: true, message: "Last name is required" } }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  dataTestmation="gender"
                  placeholder={t("SelectGender")}
                  name="gender"
                  control={control}
                  label={t("Gender")}
                  errors={errors}
                  fullWidth
                  select
                  options={[
                    { value: Gender.Male, label: "Male" },
                    { value: Gender.Female, label: "Female" },
                    { value: Gender.Other, label: "Other" }
                  ]}
                  rules={{ required: { value: true, message: "Gender is required" } }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="phone"
                  control={control}
                  rules={{ required: !isEmployeeOnlyTab ? "Phone is required" : false }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <MaterialUiPhoneNumber
                      data-testmation="phone"
                      placeholder="Eg: +977 9807100001"
                      onChange={onChange}
                      value={value}
                      onBlur={onBlur}
                      fullWidth
                      disableAreaCodes
                      variant="outlined"
                      defaultCountry="np"
                      errorMessages={errors?.phone}
                      error={!!errors?.phone}
                      label={t("MobNo.")}
                      helperText={errors?.phone?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="email"
                  name="email"
                  control={control}
                  label={t("email")}
                  errors={errors}
                  fullWidth
                  placeholder="Eg: email@domain.com"
                  rules={{
                    validate: (value) =>
                      (!value && isEmployeeOnlyTab ? true : validateEmail(value)) as any,
                    required: !isEmployeeOnlyTab
                      ? { value: true, message: "Email is required" }
                      : false
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="dateOfJoining"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CalendarDropdown
                      date={value ? new Date(value) : new Date()}
                      label="Date of Joining"
                      fullwidth
                      onChange={(v) => onChange(v)}
                      TextFieldProps={{ margin: "dense", variant: "outlined" }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CalendarDropdown
                      date={value ? new Date(value) : new Date()}
                      label="Date of Birth"
                      fullwidth
                      onChange={(v) => onChange(v)}
                      TextFieldProps={{ margin: "dense", variant: "outlined" }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="address"
                  placeholder={t("AddressPlc")}
                  name="address"
                  control={control}
                  label={t("Address")}
                  errors={errors}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="panNumber"
                  placeholder={t("panNumber")}
                  name="panNumber"
                  control={control}
                  label={t("panNumber")}
                  errors={errors}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }}>
              <Chip label="Emergency Contact Details" size="small" />
            </Divider>
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="emergencyContactName"
                  placeholder="Emergency Contact Name"
                  name="emergencyContact.contactName"
                  control={control}
                  label="Contact Name"
                  errors={errors}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="emergencyContact.contactNumber"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <MaterialUiPhoneNumber
                      data-testmation="emergencyContactNumber"
                      placeholder="Eg: +977 9807100001"
                      onChange={onChange}
                      value={value}
                      onBlur={onBlur}
                      fullWidth
                      disableAreaCodes
                      variant="outlined"
                      defaultCountry="np"
                      errorMessages={errors?.emergencyContactNumber || null}
                      error={!!errors?.emergencyContactNumber}
                      label="Contact Number"
                      helperText={errors?.emergencyContactNumber?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }}>
              <Chip label="Bank Details" size="small" />
            </Divider>
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="bankName"
                  placeholder="Bank Name"
                  name="bankDetails.name"
                  control={control}
                  label="Bank Name"
                  errors={errors}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="accountNumber"
                  placeholder="Account Number"
                  name="bankDetails.accountNumber"
                  control={control}
                  label="Account Number"
                  errors={errors}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="bankAddress"
                  placeholder="Bank Address"
                  name="bankDetails.address"
                  control={control}
                  label="Bank Address"
                  errors={errors}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormTextField
                  dataTestmation="beneficiaryName"
                  placeholder="Beneficiary Name"
                  name="bankDetails.beneficiaryName"
                  control={control}
                  label="Beneficiary Name"
                  errors={errors}
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }}>
              <Chip label="Shift Settings" size="small" />
            </Divider>
            <Grid container columnSpacing={2} rowSpacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  dataTestmation="defaultShiftSelect"
                  placeholder="Default Shift"
                  name="defaultShiftId"
                  control={control}
                  label="Default Shift"
                  errors={errors}
                  fullWidth
                  select
                  options={shifts.map((shift) => ({ value: shift.id, label: shift.name }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormTextField
                  dataTestmation="biometricDeviceCode"
                  placeholder="Biometric Code"
                  name="biometricDeviceCode"
                  control={control}
                  label="Biometric Code"
                  errors={errors}
                  fullWidth
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  Weekly Offs
                </Typography>
                <FormGroup row>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day, index) => (
                    <Controller
                      key={day}
                      name="defaultWeeklyOffs"
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={field.value?.includes(index)}
                              onChange={(e) => {
                                const newValue = e.target.checked
                                  ? [...(field.value || []), index]
                                  : field.value?.filter((v: number) => v !== index);
                                field.onChange(newValue);
                              }}
                            />
                          }
                          label={<Typography variant="body2">{day}</Typography>}
                        />
                      )}
                    />
                  ))}
                </FormGroup>
              </Grid>
            </Grid>

            {isAccountSubscribed && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Chip label="Account Details" size="small" />
                </Divider>
                <Grid container columnSpacing={2} rowSpacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormTextField
                      dataTestmation="openingBalance"
                      placeholder={t("OpeningBalancePlc")}
                      name="openingBalance"
                      control={control}
                      label={t("OpeningBalance")}
                      errors={errors}
                      fullWidth
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="openingBalanceDate"
                      control={control}
                      rules={{
                        validate: (value) =>
                          (value ? !!isISODateString(value) : true) || "Please enter valid date"
                      }}
                      render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
                        <ToggleInputDate
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...(rest as any)}
                          key="openingBalanceDate"
                          field={
                            {
                              value: openingBalanceDate,
                              key: "openingBalanceDate",
                              label: "openingBalanceDate",
                              inputType: "bsDate",
                              editable: true,
                              creatable: true,
                              required: false,
                              showAgeField: false,
                              formatter: (dateString: any): string => {
                                if (!dateString) return dateString;
                                const isValid = moment(dateString).toISOString() === dateString;
                                if (!isValid) return dateString;
                                return convertADtoBS(moment.utc(dateString)).formatted4;
                              },
                              reverseFormatter: (dateString: any): string => {
                                if (!dateString) return "";
                                const isValid = isBSDate({ msg: "" })(dateString);
                                if (!dateString || !isValid) return dateString as string;
                                const [year, month, date] = (dateString as string).split("-");
                                return moment
                                  .utc(
                                    moment(
                                      convertBStoAD(Number(year), Number(month), Number(date))
                                    ).format("YYYYMMDD")
                                  )
                                  .toISOString();
                              },
                              ADFormatter: (dateString: any): string => {
                                if (!dateString) return "";
                                const isValid = isADDate({ msg: "" })(dateString);
                                if (!dateString || !isValid) return dateString as string;
                                return moment.utc(dateString as string).toISOString();
                              }
                            } as any
                          }
                          data={{ openingBalanceDate }}
                          changeDate={(value) => onChange(value)}
                          showAgeField={false}
                          error={!!error}
                          helperText={error?.message || ""}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormTextField
                      dataTestmation="transactionType"
                      placeholder={t("transactionTypePlc")}
                      name="transactionType"
                      control={control}
                      label={t("transactionType")}
                      errors={errors}
                      fullWidth
                      select
                      options={[
                        { value: TransactionType.DEBIT, label: startCase(TransactionType.DEBIT) },
                        { value: TransactionType.CREDIT, label: startCase(TransactionType.CREDIT) }
                      ]}
                    />
                  </Grid>

                  {employeeLedger && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormTextField
                        dataTestmation="ledger"
                        placeholder={t("ledger")}
                        name="ledgerId"
                        control={control}
                        label={t("ledger")}
                        errors={errors}
                        fullWidth
                        select
                        options={[{ value: employeeLedger.id, label: employeeLedger.ledgerName }]}
                      />
                    </Grid>
                  )}
                </Grid>
              </>
            )}
          </form>
        </Box>
      </Panel>
    </Can>
  );
}
